import { findChatById } from '@/lib/chatIndex';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { StyleSheet, View, Pressable, ScrollView, Platform, Animated, LayoutAnimation } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MessageContentRenderer from './MessageContentRenderer';
import { MESSAGE_LAYOUT } from '@/constants/config';
import { useChatStore, useUIStore } from '@/store';
import { findParentUserMessage, isAutoModeAttribution } from '@/constants/utils/message';
import PropTypes from 'prop-types';

const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * True when this assistant turn's parent user message was sent in Auto mode.
 * Returns a boolean so the store subscription doesn't re-render on every
 * message-object identity change while tokens stream in.
 */
function selectParentIsAutoMode(state, conversationId, messageId) {
  if (!conversationId || !messageId) return false;
  const chat = findChatById(state.chats, conversationId);
  const messages = chat?.messages;
  if (!messages?.length) return false;

  const message = messages.find((m) => m.id === messageId);
  if (!message || message.role === 'user') return false;

  return isAutoModeAttribution(findParentUserMessage(messages, message), message);
}

// Debounce delay for transitioning from thinking to thought state
const THINKING_DEBOUNCE_MS = 800;

const STATUS_STAGE_FALLBACKS = {
  start: 'Starting your request…',
  intent: 'Planning next steps…',
  preprocess: 'Gathering information…',
  preparation: 'Preparing response…',
  execution: 'Drafting response…',
  extraction: 'Processing results…',
  heartbeat: 'Working on your request…',
  unknown: 'Working on your request…',
};

const getStageFallback = (stage) => {
  const normalizedStage = (stage || '').trim().toLowerCase();
  if (!normalizedStage) return '';
  return STATUS_STAGE_FALLBACKS[normalizedStage] || STATUS_STAGE_FALLBACKS.unknown;
};

// Rotating phrases for thinking state (used when no backend status is provided)
const THINKING_PHRASES = [
  'Starting your request…',
  'Planning next steps…',
  'Gathering information…',
  'Preparing response…',
  'Drafting response…',
  'Processing results…',
];

// Create styles outside component
const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 12,
    borderWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    minHeight: 44,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    // flexBasis 'auto' (instead of flex: 1) so the header contributes its
    // real content width during native intrinsic sizing; otherwise the
    // bubble collapses and the Show button overlaps the text.
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 0,
  },
  iconContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  headerTextContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 0,
  },
  headerPrimaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
    width: '100%',
    minWidth: 0,
  },
  statusTextWrapper: {
    flexShrink: 1,
    minWidth: 0,
  },
  headerText: {
    marginRight: 4,
    flexShrink: 1,
  },
  statusText: {
    marginLeft: 0,
    opacity: 0.7,
    flexShrink: 1,
  },
  dotsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 4,
  },
  dot: {
    fontSize: 12,
    marginHorizontal: 1,
  },
  contentWrapper: {
    position: 'relative',
  },
  content: {
    paddingHorizontal: 12,
  },
  contentContainer: {
    paddingTop: 0,
    paddingBottom: 12,
  },
  expandButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    marginLeft: 8,
  },
  expandButtonText: {
    fontSize: 12,
  },
  scrollFadeTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 20,
    zIndex: 1,
  },
  scrollFadeBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 24,
    zIndex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingBottom: 4,
  },
  scrollIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  scrollIndicatorText: {
    fontSize: 10,
    fontWeight: '500',
  },
  divider: {
    height: 1,
    marginHorizontal: 12,
  },
});

// Custom hook to stabilize the isThinking state.
//
// Streaming for "thinking" models can deliver thinking and content tokens
// interleaved (or with late-arriving thinking events after content has started),
// which causes raw `isThinking` to oscillate between true/false as the socket
// flushes its buffer. To prevent the header from flashing between
// "Thinking" and "Thought" while tokens stream in, we:
//   1. Debounce the true → false transition by THINKING_DEBOUNCE_MS so brief
//      false dips while still actively thinking don't flip the UI.
//   2. Once that debounce completes (i.e., thinking has truly ended), latch
//      to false permanently for this component instance. Subsequent
//      isThinking=true events (e.g., late thinking tokens during content
//      streaming) are ignored.
const useStabilizedThinking = (isThinking) => {
  const [stabilizedIsThinking, setStabilizedIsThinking] = useState(isThinking);
  const debounceTimerRef = useRef(null);
  const hasEverBeenThinking = useRef(false);
  const hasLatchedToFalse = useRef(false);

  useEffect(() => {
    if (hasLatchedToFalse.current) {
      return;
    }

    if (isThinking) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      hasEverBeenThinking.current = true;
      setStabilizedIsThinking(true);
    } else if (hasEverBeenThinking.current) {
      if (debounceTimerRef.current) return;

      debounceTimerRef.current = setTimeout(() => {
        hasLatchedToFalse.current = true;
        setStabilizedIsThinking(false);
        debounceTimerRef.current = null;
      }, THINKING_DEBOUNCE_MS);
    }
  }, [isThinking]);

  useEffect(() => () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  return stabilizedIsThinking;
};

// Animated pulsing icon for thinking state
const ThinkingIcon = React.memo(({ color, backgroundColor, isThinking }) => {
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const glowAnim = useRef(new Animated.Value(1)).current;
  const animationsRef = useRef(null);
  
  useEffect(() => {
    if (animationsRef.current) {
      animationsRef.current.stop();
      animationsRef.current = null;
    }
    
    if (isThinking) {
      pulseAnim.setValue(1);
      glowAnim.setValue(0.7);
      
      const pulseAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.1,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      
      const glowAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(glowAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(glowAnim, {
            toValue: 0.7,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      );
      
      animationsRef.current = Animated.parallel([pulseAnimation, glowAnimation]);
      animationsRef.current.start();
    } else {
      Animated.parallel([
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(glowAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
    
    return () => {
      if (animationsRef.current) {
        animationsRef.current.stop();
      }
    };
  }, [isThinking, pulseAnim, glowAnim]);
  
  return (
    <Animated.View 
      style={[
        styles.iconContainer,
        { 
          backgroundColor,
          transform: [{ scale: pulseAnim }],
          opacity: glowAnim,
        }
      ]}
    >
      <Icon 
        name={isThinking ? 'brain' : 'lightbulb'}
        size={14} 
        color={color}
        accessibilityLabel={isThinking ? 'Thinking in progress' : 'Thinking complete'}
      />
    </Animated.View>
  );
});

ThinkingIcon.displayName = 'ThinkingIcon';

// Animated dots component for thinking state
const AnimatedDots = React.memo(({ color }) => {
  const dot1 = useRef(new Animated.Value(0.3)).current;
  const dot2 = useRef(new Animated.Value(0.3)).current;
  const dot3 = useRef(new Animated.Value(0.3)).current;
  const animationsRef = useRef([]);
  
  useEffect(() => {
    const createDotAnimation = (dot, delay) => {
      return Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(dot, {
            toValue: 0.3,
            duration: 400,
            useNativeDriver: true,
          }),
        ])
      );
    };
    
    animationsRef.current = [
      createDotAnimation(dot1, 0),
      createDotAnimation(dot2, 150),
      createDotAnimation(dot3, 300),
    ];
    
    Animated.parallel(animationsRef.current).start();
    
    return () => {
      animationsRef.current.forEach(anim => anim.stop());
      dot1.setValue(0.3);
      dot2.setValue(0.3);
      dot3.setValue(0.3);
    };
  }, [dot1, dot2, dot3]);
  
  return (
    <View style={styles.dotsContainer}>
      {[dot1, dot2, dot3].map((dot, index) => (
        <Animated.Text
          key={index}
          style={[styles.dot, { color, opacity: dot }]}
        >
          •
        </Animated.Text>
      ))}
    </View>
  );
});

AnimatedDots.displayName = 'AnimatedDots';

// Live duration counter
const LiveDuration = React.memo(({ startTime, isThinking, finalDurationMs }) => {
  const [displaySeconds, setDisplaySeconds] = useState(() => {
    if (finalDurationMs && finalDurationMs > 0) {
      return Math.ceil(finalDurationMs / 1000);
    }
    if (startTime) {
      return Math.max(1, Math.ceil((Date.now() - startTime) / 1000));
    }
    return 1;
  });
  
  useEffect(() => {
    if (finalDurationMs && finalDurationMs > 0) {
      setDisplaySeconds(Math.ceil(finalDurationMs / 1000));
      return;
    }
    
    if (!isThinking) {
      return;
    }
    
    const interval = setInterval(() => {
      if (startTime) {
        setDisplaySeconds(Math.max(1, Math.ceil((Date.now() - startTime) / 1000)));
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isThinking, startTime, finalDurationMs]);
  
  return <Text size="n" color="primary" weight="medium" numberOfLines={1}>{displaySeconds}s</Text>;
});

LiveDuration.displayName = 'LiveDuration';

// Memoized header component
const ThinkingHeader = React.memo(({ 
  isThinking, 
  thinkingDurationMs,
  thinkingStartTime,
  inferenceStatus,
  inferenceStage,
  isExpanded, 
  colors,
  onPress,
  hasContent,
}) => {
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const statusFadeAnim = useRef(new Animated.Value(1)).current;
  const prevIsThinking = useRef(isThinking);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  
  useEffect(() => {
    if (prevIsThinking.current && !isThinking) {
      Animated.sequence([
        Animated.timing(fadeAnim, {
          toValue: 0.7,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    }
    prevIsThinking.current = isThinking;
  }, [isThinking, fadeAnim]);

  const hasBackendStatus = Boolean(inferenceStatus || inferenceStage);

  // Rotate phrases every 3 seconds when thinking and no backend status
  useEffect(() => {
    if (!isThinking || hasBackendStatus) return;
    
    const interval = setInterval(() => {
      // Fade out
      Animated.timing(statusFadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start(() => {
        // Change phrase
        setCurrentPhraseIndex(prev => (prev + 1) % THINKING_PHRASES.length);
        // Fade in
        Animated.timing(statusFadeAnim, {
          toValue: 1,
          duration: 150,
          useNativeDriver: true,
        }).start();
      });
    }, 3000);

    return () => {
      clearInterval(interval);
      statusFadeAnim.stopAnimation();
    };
  }, [isThinking, hasBackendStatus, statusFadeAnim]);

  // Reset phrase index when thinking stops
  useEffect(() => {
    if (!isThinking) {
      setCurrentPhraseIndex(0);
      statusFadeAnim.setValue(1);
    }
  }, [isThinking, statusFadeAnim]);
  
  // Dynamic colors based on thinking state
  const iconBgColor = isThinking 
    ? colors.solid_color_toast 
    : colors.solid_color_secondary;
  const iconColor = isThinking 
    ? colors.text_color_alt 
    : colors.text_color_primary;

  const stageFallback = getStageFallback(inferenceStage);
  const displayStatus =
    inferenceStatus ||
    stageFallback ||
    (isThinking ? THINKING_PHRASES[currentPhraseIndex] : null);
  
  return (
    <Pressable
      style={styles.header}
      onPress={hasContent ? onPress : undefined}
      disabled={!hasContent}
    >
      <Animated.View style={[styles.headerLeft, { opacity: fadeAnim }]}>
        <ThinkingIcon 
          color={iconColor} 
          backgroundColor={iconBgColor}
          isThinking={isThinking} 
        />
        <View style={styles.headerTextContainer}>
          <View style={styles.headerPrimaryRow}>
            <Text
              style={styles.headerText}
              size="n"
              color="primary"
              weight="medium"
              accessibilityRole="text"
              numberOfLines={1}
            >
              {isThinking ? 'Thinking' : 'Thought'} for{' '}
            </Text>
            <LiveDuration 
              startTime={thinkingStartTime}
              isThinking={isThinking}
              finalDurationMs={thinkingDurationMs}
            />
          </View>
          {isThinking && displayStatus && (
            <View style={styles.statusRow}>
              <Animated.View style={[styles.statusTextWrapper, { opacity: hasBackendStatus ? 1 : statusFadeAnim }]}>
                <Text size="s" color="secondary" style={styles.statusText} numberOfLines={1}>
                  — {displayStatus}
                </Text>
              </Animated.View>
              <AnimatedDots color={colors.text_color_secondary} />
            </View>
          )}
        </View>
      </Animated.View>
      {hasContent && (
        <Pressable 
          style={[styles.expandButton, { backgroundColor: colors.solid_color_primary }]} 
          onPress={onPress}
        >
          <Text style={[styles.expandButtonText, { color: colors.text_color_secondary }]}>
            {isExpanded ? 'Hide' : 'Show'}
          </Text>
          <Icon 
            name={isExpanded ? 'chevron-up' : 'chevron-down'} 
            size={12} 
            color={colors.text_color_secondary}
            accessibilityLabel={isExpanded ? 'Collapse content' : 'Expand content'}
          />
        </Pressable>
      )}
    </Pressable>
  );
});

ThinkingHeader.propTypes = {
  isThinking: PropTypes.bool,
  thinkingDurationMs: PropTypes.number,
  thinkingStartTime: PropTypes.number,
  inferenceStatus: PropTypes.string,
  inferenceStage: PropTypes.string,
  isExpanded: PropTypes.bool,
  colors: PropTypes.object,
  onPress: PropTypes.func,
  hasContent: PropTypes.bool,
};

ThinkingHeader.displayName = 'ThinkingHeader';

// Memoized content component with scroll indicators
const ThinkingContentView = React.memo(({
  conversationId,
  messageId,
  thinkingContent, 
  isThinking, 
  theme, 
  maxHeight,
  colors,
}) => {
  const scrollViewRef = useRef(null);
  const prevContentLength = useRef(0);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  
  // Auto-scroll to bottom when new content streams in during thinking
  useEffect(() => {
    if (isThinking && thinkingContent && thinkingContent.length > prevContentLength.current) {
      const timeout = setTimeout(() => {
        scrollViewRef.current?.scrollToEnd?.({ animated: true });
      }, 100);
      prevContentLength.current = thinkingContent.length;
      return () => clearTimeout(timeout);
    }
  }, [isThinking, thinkingContent]);
  
  // Check if content is scrollable and update fade indicators
  useEffect(() => {
    const isScrollable = contentHeight > scrollViewHeight;
    setShowBottomFade(isScrollable);
  }, [contentHeight, scrollViewHeight]);
  
  const handleScroll = useCallback((event) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const isAtTop = contentOffset.y <= 5;
    const isAtBottom = contentOffset.y + layoutMeasurement.height >= contentSize.height - 5;
    
    setShowTopFade(!isAtTop && contentSize.height > layoutMeasurement.height);
    setShowBottomFade(!isAtBottom && contentSize.height > layoutMeasurement.height);
  }, []);
  
  const handleContentSizeChange = useCallback((width, height) => {
    setContentHeight(height);
  }, []);
  
  const handleLayout = useCallback((event) => {
    setScrollViewHeight(event.nativeEvent.layout.height);
  }, []);
  
  const isScrollable = contentHeight > scrollViewHeight;
  
  return (
    <View style={styles.contentWrapper}>
      {/* Divider line between header and content */}
      <View style={[styles.divider, { backgroundColor: colors.border_color_input }]} />
      
      {/* Top fade gradient */}
      {showTopFade && (
        <View 
          style={[
            styles.scrollFadeTop, 
            { 
              backgroundColor: colors.solid_color_tertiary,
              opacity: 0.95,
            }
          ]} 
        />
      )}
      
      <ScrollView
        ref={scrollViewRef}
        style={[styles.content, { maxHeight }]}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={true}
        scrollEnabled={true}
        bounces={true}
        nestedScrollEnabled={true}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        scrollEventThrottle={16}
        accessibilityLabel="Thinking content - scrollable"
      >
        <MessageContentRenderer
          conversationId={conversationId}
          messageId={messageId}
          content={thinkingContent}
          theme={theme}
          isStreaming={isThinking}
        />
      </ScrollView>
      
      {/* Bottom fade with scroll indicator */}
      {showBottomFade && (
        <View 
          style={[
            styles.scrollFadeBottom,
            {
              background: Platform.OS === 'web' 
                ? `linear-gradient(transparent, ${colors.solid_color_tertiary})`
                : undefined,
              backgroundColor: Platform.OS !== 'web' ? colors.solid_color_tertiary : undefined,
            }
          ]}
        >
          <View style={[styles.scrollIndicator, { backgroundColor: colors.solid_color_primary }]}>
            <Icon name="chevron-down" size={10} color={colors.text_color_secondary} />
            <Text style={[styles.scrollIndicatorText, { color: colors.text_color_secondary }]}>
              Scroll for more
            </Text>
          </View>
        </View>
      )}
    </View>
  );
});

ThinkingContentView.displayName = 'ThinkingContentView';

function MessageThinkingContent({
  conversationId,
  messageId,
  thinkingContent,
  thinkingDurationMs,
  thinkingStartTime,
  isThinking,
  inferenceStatus,
  inferenceStage,
  messageLayout,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { colors, theme: themeProp } = useTheme();
  const theme = useMemo(() => ({ colors, theme: themeProp }), [colors, themeProp]);
  const wasExpandedDuringThinking = useRef(false);

  // Native Auto replies: skip the post-inference "Thought for Xs" pill.
  // Inserting it after the answer lands causes a janky reflow on iOS/Android.
  // Web and explicit model selections keep the trace. Auto is persisted on
  // the parent turn so changing the current composer cannot rewrite history.
  const parentIsAutoMode = useChatStore((state) => (
    selectParentIsAutoMode(state, conversationId, messageId)
  ));
  const hideNativeAutoTrace = isNativeMobile && parentIsAutoMode;
  
  // Use stabilized thinking state to prevent flickering between
  // "Thinking" and "Thought" when thinking and content tokens arrive
  // interleaved (or with late-arriving thinking events).
  const stabilizedIsThinking = useStabilizedThinking(isThinking);

  const searchQuery = useUIStore(state => (
    Platform.OS === 'web' && state.highlightedMessageId === messageId
      ? state.highlightedSearchQuery : null
  ));
  const searchRequestAt = useUIStore(state => (
    Platform.OS === 'web'
      && state.pendingScrollRequest?.conversationId === conversationId
      && state.pendingScrollRequest?.messageId === messageId
      && state.pendingScrollRequest?.searchQuery
      ? state.pendingScrollRequest.requestedAt : null
  ));
  const lastSearchReveal = useRef(null);
  useEffect(() => {
    if (!searchQuery) { lastSearchReveal.current = null; return; }
    if (stabilizedIsThinking || !thinkingContent?.toLowerCase().includes(searchQuery.toLowerCase())) return;
    const previous = lastSearchReveal.current;
    // Clearing the completed request must not reopen a manually closed panel.
    // A fresh next/previous request may reveal the same query again.
    if (previous?.query === searchQuery && (!searchRequestAt || previous.requestedAt === searchRequestAt)) return;
    lastSearchReveal.current = { query: searchQuery, requestedAt: searchRequestAt };
    setIsExpanded(true);
  }, [searchQuery, searchRequestAt, thinkingContent, stabilizedIsThinking]);
  
  // Track when thinking starts for the live counter
  const thinkingStartTimeRef = useRef(thinkingStartTime || null);
  
  useEffect(() => {
    if (stabilizedIsThinking && !thinkingStartTimeRef.current) {
      thinkingStartTimeRef.current = Date.now();
    }
  }, [stabilizedIsThinking]);
  
  // Track if user expanded during thinking
  useEffect(() => {
    if (stabilizedIsThinking && isExpanded) {
      wasExpandedDuringThinking.current = true;
    }
  }, [stabilizedIsThinking, isExpanded]);
  
  // Auto-collapse when thinking completes (only if user had expanded it)
  // NOTE: We intentionally don't use LayoutAnimation here to prevent scroll jumps
  // during streaming. The collapse happens instantly which is less disruptive.
  useEffect(() => {
    if (!stabilizedIsThinking && wasExpandedDuringThinking.current && isExpanded) {
      setIsExpanded(false);
      wasExpandedDuringThinking.current = false;
    }
  }, [stabilizedIsThinking, isExpanded]);

  // Memoize toggle handler - skip animation during streaming to prevent scroll jumps
  const handleToggle = useCallback(() => {
    // Only use LayoutAnimation when not actively thinking/streaming
    // During streaming, instant state change is less disruptive to scroll position
    if (!stabilizedIsThinking) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    setIsExpanded(prev => !prev);
  }, [stabilizedIsThinking]);

  // Only show after inference finishes — hiding mid-stream avoids flicker
  // between waiting/processing and the thinking panel while CoT is live.
  // Call sites already gate on !isThinking/!isStreaming; this component is the
  // post-inference "Thought for Xs" UI.
  const shouldShow = !hideNativeAutoTrace && !stabilizedIsThinking && !!thinkingContent;
  
  // Return early if nothing to show
  if (!shouldShow) {
    return null;
  }
  
  const hasContent = !!thinkingContent && thinkingContent.length > 0;
  const maxHeight = isExpanded ? (stabilizedIsThinking ? 180 : 350) : 0;
  
  // Use distinct colors for the thinking box - slightly tinted background
  const backgroundColor = colors.solid_color_tertiary;
  const borderColor = stabilizedIsThinking 
    ? colors.border_color_input 
    : colors.border_color_secondary;

  return (
    <View 
      style={[
        styles.container, 
        { 
          backgroundColor,
          borderColor,
        }
      ]}
    >
      <ThinkingHeader
        isThinking={stabilizedIsThinking}
        thinkingDurationMs={thinkingDurationMs}
        thinkingStartTime={thinkingStartTimeRef.current}
        inferenceStatus={stabilizedIsThinking ? inferenceStatus : null}
        inferenceStage={stabilizedIsThinking ? inferenceStage : null}
        onPress={handleToggle}
        isExpanded={isExpanded}
        hasContent={hasContent}
        colors={colors}
      />
      
      {isExpanded && hasContent && (
        <ThinkingContentView
          conversationId={conversationId}
          messageId={messageId}
          thinkingContent={thinkingContent}
          isThinking={stabilizedIsThinking}
          theme={theme}
          maxHeight={stabilizedIsThinking ? 180 : 350}
          colors={colors}
        />
      )}
    </View>
  );
}

MessageThinkingContent.propTypes = {
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
  thinkingContent: PropTypes.string,
  thinkingDurationMs: PropTypes.number,
  thinkingStartTime: PropTypes.number,
  isThinking: PropTypes.bool,
  inferenceStatus: PropTypes.string,
  inferenceStage: PropTypes.string,
  messageLayout: PropTypes.string,
};

MessageThinkingContent.displayName = 'MessageThinkingContent';

// Custom comparison function for React.memo
function arePropsEqual(prevProps, nextProps) {
  return (
    prevProps.thinkingContent === nextProps.thinkingContent &&
    prevProps.isThinking === nextProps.isThinking &&
    prevProps.thinkingDurationMs === nextProps.thinkingDurationMs &&
    prevProps.thinkingStartTime === nextProps.thinkingStartTime &&
    prevProps.inferenceStatus === nextProps.inferenceStatus &&
    prevProps.inferenceStage === nextProps.inferenceStage &&
    prevProps.messageLayout === nextProps.messageLayout &&
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.messageId === nextProps.messageId
  );
}

export default React.memo(MessageThinkingContent, arePropsEqual);
